import React, { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Skeleton } from '@node-space/storybook-components/dist/SkeletonLoader'
import { Box, ChipList, ChipProps, RowsSkeletonLoader } from '@node-space/ui-components'
import NoSearchResults from 'components/NoSearchResults/NoSearchResults'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { BeneficiaryDetails } from 'types/beneficiaries'
import { SourcePayWallet } from '../../Pay/types'
import BeneficiaryFormCallout from './BeneficiaryFormCallout'
import BeneficiarySearchList from './BeneficiarySearchList'

interface BeneficiaryContentProps {
  fiatBeneficiaries: BeneficiaryDetails[] | null
  filters: ChipProps[]
  isFetchingFilters: boolean
  isFetchingBeneficiaries: boolean
  isInsufficientBalance: boolean
  isPayoutsSupported: boolean
  sourceWalletAtom: SourcePayWallet
  handleBeneficiarySelect: (beneficiary: BeneficiaryDetails) => void
  handleBeneficiaryFilterChange: (selectedFilter: string[]) => void
  setSearchQuery: (searchQuery: string) => void
}

const BeneficiaryContent = ({
  fiatBeneficiaries,
  filters,
  isFetchingFilters,
  isFetchingBeneficiaries,
  isInsufficientBalance,
  isPayoutsSupported,
  sourceWalletAtom,
  handleBeneficiarySelect,
  handleBeneficiaryFilterChange,
  setSearchQuery,
}: BeneficiaryContentProps) => {
  const { t } = useTranslation()
  const isLoadingListContent = useMemo(() => {
    return isFetchingFilters || isFetchingBeneficiaries
  }, [isFetchingFilters, isFetchingBeneficiaries, fiatBeneficiaries?.length])

  const showBeneficiaryList = useMemo(() => {
    return (
      !isLoadingListContent &&
      isPayoutsSupported &&
      !isInsufficientBalance &&
      !!fiatBeneficiaries?.length
    )
  }, [isPayoutsSupported, isInsufficientBalance, isLoadingListContent, fiatBeneficiaries])

  const showEmptyState = useMemo(() => {
    return (
      !isLoadingListContent &&
      isPayoutsSupported &&
      !isInsufficientBalance &&
      !!fiatBeneficiaries &&
      !fiatBeneficiaries?.length
    )
  }, [isPayoutsSupported, isInsufficientBalance, isLoadingListContent, fiatBeneficiaries])

  const showCallout = useMemo(() => {
    return !isLoadingListContent && (!isPayoutsSupported || isInsufficientBalance)
  }, [isPayoutsSupported, isInsufficientBalance, isLoadingListContent])

  return (
    <Box>
      {isFetchingFilters ? (
        <Skeleton className="w-full h-4 mt-2" variant="text" />
      ) : (
        filters?.length > 1 &&
        !showCallout && (
          <Box className="mb-4">
            <ChipList
              key={sourceWalletAtom?.walletId}
              multiselect={false}
              chips={filters}
              onHandleChange={handleBeneficiaryFilterChange}
            />
          </Box>
        )
      )}

      {isLoadingListContent && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}

      {showBeneficiaryList && (
        <BeneficiarySearchList
          beneficiaries={fiatBeneficiaries}
          handleBeneficiarySelect={handleBeneficiarySelect}
        />
      )}

      {showEmptyState && (
        <NoSearchResults
          clearSearchCriteria={() => {
            setSearchQuery(null)
          }}
        />
      )}

      {showCallout && (
        <BeneficiaryFormCallout
          key={sourceWalletAtom?.walletId}
          isInsufficientBalance={isInsufficientBalance}
          isPayoutsSupported={isPayoutsSupported}
          wallet={sourceWalletAtom}
        />
      )}
    </Box>
  )
}

export default memo(BeneficiaryContent)
